import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/baseUi/input-group"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { convertTimeObjectToTimeValue, convertTimeValueToTimeObject } from "@/modules/duration/timeConverter"
import { formatNumberWith2Digits, formatNumberWithXDigits } from "@/modules/utils/numberUtils"
import { type FieldValueChange } from "@/typings/formik"
import { type FieldInputProps } from "formik"
import type { ChangeEvent } from "react"

export type HUChronoInputProps<K> = {
  name: string
  label?: string
  isRequiredInput?: boolean
  getFieldProps: (nameOrOptions: string) => FieldInputProps<number>
  setFieldValue: FieldValueChange<K>
  extraAddon?: React.ReactNode
  error?: React.JSX.Element
} & React.ComponentProps<"input">

export function HUChronoInput<K>({
  name,
  label,
  isRequiredInput,
  getFieldProps,
  setFieldValue,
  extraAddon,
  error,
  ...props
}: HUChronoInputProps<K>) {
  const fieldValue = getFieldProps(name).value
  const inputValue =
    typeof fieldValue === "number"
      ? convertTimeValueToTimeObject(fieldValue, "seconds", "minutes")
      : { days: 0, hours: 0, minutes: 0, seconds: 0 }

  const onMinutesInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (parseInt(newValue) < 0) {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, minutes: 0 }, "seconds"))
    } else if (parseInt(newValue) > 999) {
      setFieldValue(
        name,
        convertTimeObjectToTimeValue(
          {
            ...inputValue,
            minutes: "data" in event.nativeEvent ? parseInt(event.nativeEvent.data as string) : 999,
          },
          "seconds",
        ),
      )
    } else {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, minutes: parseInt(newValue) }, "seconds"))
    }
  }

  const onSecondsInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (parseInt(newValue) < 0) {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, seconds: 0 }, "seconds"))
    } else if (parseInt(newValue) > 59) {
      setFieldValue(
        name,
        convertTimeObjectToTimeValue(
          {
            ...inputValue,
            seconds: "data" in event.nativeEvent ? parseInt(event.nativeEvent.data as string) : 59,
          },
          "seconds",
        ),
      )
    } else {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, seconds: parseInt(newValue) }, "seconds"))
    }
  }

  return (
    <InputContainer isRequiredInput={isRequiredInput} error={error} {...props}>
      <InputGroup className="justify-between bg-surface-background pr-2" id={name}>
        <div className="flex flex-row items-baseline">
          <InputGroupInput
            placeholder="00"
            type="number"
            className="max-w-11 px-1 text-end"
            value={
              inputValue && inputValue.minutes !== undefined
                ? formatNumberWithXDigits(inputValue.minutes, inputValue.minutes > 99 ? 3 : 2)
                : ""
            }
            onChange={onMinutesInputChange}
          />
          <InputGroupText className="mt-0.5 text-base">{word("global.minute.shortLabel", { count: 1 })}</InputGroupText>
          <InputGroupInput
            placeholder="00"
            type="number"
            className="max-w-8 px-1 text-end"
            value={inputValue ? formatNumberWith2Digits(inputValue.seconds || 0) : ""}
            onChange={onSecondsInputChange}
          />
          <InputGroupText className="mt-0.5 text-base">
            {word("global.seconds.shortLabel", { count: 1 })[0]}
          </InputGroupText>
        </div>
        <InputGroupAddon align="inline-end" className="flex items-center gap-1">
          <Text fontStyle="BM">{label}</Text>
          {extraAddon}
        </InputGroupAddon>
      </InputGroup>
    </InputContainer>
  )
}
